import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'

import * as Yup from 'yup'
import { isEqual, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import ReactTooltip from 'react-tooltip'

import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Icon from '@material-ui/core/Icon'
import Input from '@material-ui/core/Input'
import Tooltip from '@material-ui/core/Tooltip'
import Checkbox from '@material-ui/core/Checkbox'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import { useLocation } from 'react-router-dom'
import Autocomplete from '@material-ui/lab/Autocomplete'
import makeStyles from '@material-ui/core/styles/makeStyles'

import { usePrice } from '../../../../../shared/contexts/PriceContext'
import useYup from '../../../../../shared/hooks/useYup'

import useUserClient from '../../../../../clients/UserClient/useUserClient'

import {
  ButtonLight,
  ButtonGradient,
} from '../../../../../shared/components'

import {
  QUOTE_URL_EXECUTIVE_REQUEST_JT,
  COLOR_SUCCESS,
} from '../../../../../constants'

import { GlobalMessages } from '../../../../../shared/messages'
import PhoneInput from '../../../../../shared/components/PhoneInput'
import CollapsePrice from '../../../../../shared/components/CollapsePrice/CollapsePrice'

import useSecurity from '../../../../../security/useSecurity'
import { useSecurityAction } from '../../../../../store/ducks/security'

import { ReactComponent as IcoWhats } from '../../../../../assets/img/icons/ico-whats.svg'

import RequesterData from '../../../../../models/JudicialGuaranteeLaborModels/RequesterData'

const useStyles = makeStyles((theme) => ({
  buttonsStep: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& .MuiBox-root': {
      minWidth: 190,
      [theme.breakpoints.down('xs')]: {
        marginTop: 5,
        marginLeft: 5,
        minWidth: 115,
      },
    },
  },
  label: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
  },
  whatsApplabel: {
    marginTop: 3,
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
  },
  info: {
    verticalAlign: 'bottom',
  },
  whatsappRow: {
    display: 'flex',
    alignItems: 'center',
  },
  whatsappIcon: {
    width: 22,
    height: 22,
    marginRight: 8,
    flexShrink: 0,
  },
  checkboxCheked: {
    '& .MuiCheckbox-colorSecondary.Mui-checked': {
      color: COLOR_SUCCESS,
    },
  },
}))

const Requester = forwardRef((props, ref) => {
  const classes = useStyles()

  const {
    quote,
    checked,
    handleStep,
    handleNext,
    handlePrevious,
  } = props

  const userClient = useUserClient()

  const { phoneOrCellphone } = useYup()

  const {
    onNext,
    actionNext,
    setFormikChanged,
    judicialGuaranteeLabor,
    setJudicialGuaranteeLabor,
  } = usePrice()

  const location = useLocation()

  const getUrl = location?.pathname?.split(':')
  const routeExecutiveRequest = getUrl[0]

  const {
    getName,
    getEmail,
    getPhone,
    getUser,
  } = useSecurity()

  const { setUser } = useSecurityAction()

  const [openTooltip, setOpenTooltip] = useState(false)
  const [clientEmails, setClientEmails] = useState([])

  const isExecutiveRoute = QUOTE_URL_EXECUTIVE_REQUEST_JT === routeExecutiveRequest

  useEffect(() => {
    if ((QUOTE_URL_EXECUTIVE_REQUEST_JT === routeExecutiveRequest)
      && judicialGuaranteeLabor && judicialGuaranteeLabor.policyHolder
      && judicialGuaranteeLabor.policyHolder.document) {
      const clientDocument = judicialGuaranteeLabor.policyHolder.document
      userClient().findUserEmailsByClient(clientDocument).then((response) => {
        setClientEmails(response.data)
      })
    }
  }, [
    userClient,
    judicialGuaranteeLabor,
    judicialGuaranteeLabor?.policyHolder,
    judicialGuaranteeLabor?.policyHolder?.document,
    routeExecutiveRequest,
  ])

  const toggleClick = (bool) => {
    setOpenTooltip(bool)
  }

  const initialValuesForm = useMemo(() => ({
    ...Object.assign(
      RequesterData(quote?.Requester ?? {
        Name: (QUOTE_URL_EXECUTIVE_REQUEST_JT !== routeExecutiveRequest) ? getName() : '',
        Email: (QUOTE_URL_EXECUTIVE_REQUEST_JT !== routeExecutiveRequest) ? getEmail() : '',
        Phone: (QUOTE_URL_EXECUTIVE_REQUEST_JT !== routeExecutiveRequest) ? getPhone() : '',
      }),
    ),
    sendWhatsapp: !!(quote?.Requester?.MobilePhone
      ?? judicialGuaranteeLabor?.requester?.MobilePhone),
    mobilePhone: quote?.Requester?.MobilePhone
      ?? judicialGuaranteeLabor?.requester?.MobilePhone
      ?? getUser()?.mobilePhone
      ?? '',
  }), [
    quote?.Requester,
    getName,
    getEmail,
    getPhone,
    getUser,
    judicialGuaranteeLabor?.requester?.MobilePhone,
    routeExecutiveRequest])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesForm,
    validationSchema: Yup.object({
      note: Yup.string(),
      phone: phoneOrCellphone.required(),
      name: Yup.string().max(200).required(),
      email: Yup.string().email().required(),
      customerControlIdentifier: Yup.string().max(100),
      sendWhatsapp: Yup.boolean(),
      mobilePhone: Yup.string().when('sendWhatsapp', (sendWhatsapp, schema) => (
        sendWhatsapp ? phoneOrCellphone.required() : schema
      )),
    }),
    onSubmit: (data) => {
      const { sendWhatsapp, mobilePhone, ...rest } = data
      const cleanPhone = mobilePhone?.replace(/[^0-9]/g, '')

      const requesterData = {
        ...rest,
        ...(sendWhatsapp && cleanPhone ? { MobilePhone: cleanPhone } : {}),
      }

      const values = {
        ...judicialGuaranteeLabor,
        ...{ requester: requesterData },
      }
      setJudicialGuaranteeLabor(values)

      if (sendWhatsapp && cleanPhone) {
        const currentUser = getUser() || {}
        const userMobilePhone = (currentUser.mobilePhone ?? '').replace(/[^0-9]/g, '')
        if (cleanPhone !== userMobilePhone) {
          setUser({ ...currentUser, mobilePhone: cleanPhone })
        }
        userClient().updateContactPhone(cleanPhone)
      }

      if (actionNext) {
        setFormikChanged(!isEqual(initialValuesForm, data))
        onNext(values)
      }
    },
  })

  useImperativeHandle(ref, () => ({
    onSubmit: () => new Promise((resolve) => {
      formik.validateForm().then((errors) => {
        let data

        if (!isEmpty(errors)) {
          data = { message: GlobalMessages.VERIFIQUE_CAMPOS_DESTAQUE_ANTES_PROSSEGUIR }
        }

        resolve(data)
        formik.submitForm()
      })
    }),
  }))

  const noteLabel = 'Observações'
  const requesterLabel = 'Nome do Solicitante'
  const phoneLabel = 'Telefone do Solicitante'
  const idClientControlLabel = 'Identificador do Controle do Cliente'
  const clientEmailLabel = 'Email do Solicitante'

  return (
    <>
      <CollapsePrice
        checked={checked}
        handleEdit={handleStep}
        hiddenEdit={isEmpty(quote?.Requester) && !formik.isSubmitting}
        title="Solicitante"
        subtitle="Informe abaixo os dados do Solicitante"
      >
        <Grid container spacing={5}>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="requesterLabel" className={classes.label}>{requesterLabel}</label>
              <Input
                fullWidth
                id="name"
                color="secondary"
                disabled={!isExecutiveRoute && !!getName()}
                value={getName()}
                {...formik.getFieldProps('name')}
                error={formik.touched.name && !!formik.errors.name}
              />
              <FormHelperText
                hidden={!formik.touched.name || !formik.errors.name}
                error={formik.touched.name && !!formik.errors.name}
              >
                {formik.errors.name}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="phoneLabel" className={classes.label}>{phoneLabel}</label>
              <PhoneInput
                id="phone"
                phoneType="auto"
                value={getPhone()}
                placeholder="(00) 0000-0000"
                disabled={!isExecutiveRoute && !!getPhone()}
                {...formik.getFieldProps('phone')}
                error={formik.touched.phone && !!formik.errors.phone}
              />
              <FormHelperText
                hidden={!formik.touched.phone || !formik.errors.phone}
                error={formik.touched.phone && !!formik.errors.phone}
              >
                {formik.errors.phone}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} style={{ alignSelf: 'flex-start' }}>
            <Box className={`${classes.whatsappRow} ${classes.checkboxCheked}`}>
              <Checkbox
                checked={formik.values.sendWhatsapp}
                style={{ padding: 0 }}
                inputProps={{ 'aria-label': 'Deseja receber o PDF da apólice por WhatsApp?' }}
                onChange={(e) => {
                  formik.setFieldValue('sendWhatsapp', e.target.checked)
                  if (!e.target.checked) {
                    formik.setFieldTouched('mobilePhone', false)
                  }
                }}
              />
              <Box component="span" className={classes.label}>
                Deseja receber o PDF da apólice por WhatsApp?
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <Box component="span" className={classes.whatsApplabel}>
                Número de WhatsApp
              </Box>
              <PhoneInput
                id="mobilePhone"
                phoneType="auto"
                placeholder="(00) 0 0000-0000"
                disabled={!formik.values.sendWhatsapp}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IcoWhats fill={COLOR_SUCCESS} className={classes.whatsappIcon} />
                    </InputAdornment>
                  ),
                }}
                {...formik.getFieldProps('mobilePhone')}
                error={formik.touched.mobilePhone && !!formik.errors.mobilePhone}
              />
              <FormHelperText
                hidden={!formik.touched.mobilePhone || !formik.errors.mobilePhone}
                error={formik.touched.mobilePhone && !!formik.errors.mobilePhone}
              >
                {formik.errors.mobilePhone}
              </FormHelperText>
            </FormControl>
          </Grid>

          {(QUOTE_URL_EXECUTIVE_REQUEST_JT === routeExecutiveRequest)
          && (
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="clientEmailLabel" className={classes.label}>{clientEmailLabel}</label>
              <Autocomplete
                id="email"
                onInputChange={(event, newValue) => {
                  formik.setFieldValue('email', newValue)
                }}
                options={clientEmails}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    error={formik.touched.email && !!formik.errors.email}
                  />
                )}
                {...formik.getFieldProps('email')}
              />
              <FormHelperText
                error={formik.touched.email && !!formik.errors.email}
              >
                {formik.errors.email}
              </FormHelperText>
            </FormControl>
          </Grid>
          )}
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label
                htmlFor="idClientControlLabel"
                className={classes.label}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {idClientControlLabel}
                <Box
                  mr={1}
                  onClick={() => toggleClick(true)}
                  onMouseOver={() => toggleClick(true)}
                >
                  <Tooltip
                    placement="right-end"
                    className={classes.info}
                    title="Número de Controle de Cliente (campo opcional)"
                    open={openTooltip}
                    onClose={() => toggleClick(false)}
                  >
                    <Icon
                      data-html
                      style={{ cursor: 'help', marginLeft: 5 }}
                    >
                      help_outline
                    </Icon>
                  </Tooltip>
                </Box>
              </label>
              <Input
                fullWidth
                id="customerControlIdentifier"
                color="secondary"
                {...formik.getFieldProps('customerControlIdentifier')}
                error={formik.touched.customerControlIdentifier
                  && !!formik.errors.customerControlIdentifier}
              />
              <FormHelperText
                hidden={!formik.touched.customerControlIdentifier
                  || !formik.errors.customerControlIdentifier}
                error={formik.touched.customerControlIdentifier
                  && !!formik.errors.customerControlIdentifier}
              >
                {formik.errors.customerControlIdentifier}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <label htmlFor="noteLabel" className={classes.label}>{noteLabel}</label>
              <TextField
                minRows={3}
                fullWidth
                multiline
                id="note"
                variant="outlined"
                {...formik.getFieldProps('note')}
                error={formik.touched.note && !!formik.errors.note}
              />
              <FormHelperText
                hidden={!formik.touched.note || !formik.errors.note}
                error={formik.touched.note && !!formik.errors.note}
              >
                {formik.errors.note}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.buttonsStep}>
              <Box>
                <ButtonLight
                  onClick={handlePrevious}
                  title="Voltar"
                >
                  Voltar
                </ButtonLight>
              </Box>
              <Box ml={4}>
                <ButtonGradient
                  onClick={handleNext}
                  title="Continuar"
                >
                  Continuar
                </ButtonGradient>
              </Box>
            </Box>
            <ReactTooltip place="top" type="dark" />
          </Grid>
        </Grid>
      </CollapsePrice>
    </>
  )
})

Requester.propTypes = {
  quote: PropTypes.any,
  checked: PropTypes.bool,
  children: PropTypes.any,
  handleStep: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  handlePrevious: PropTypes.func.isRequired,
}

Requester.defaultProps = {
  children: '',
  checked: false,
  quote: undefined,
}

export default Requester
